import { useRef } from 'react';

/**
 * Whether anything has actually changed since the form was filled in.
 *
 * These modals open on values fetched from the API, so "unchanged" cannot be
 * judged against the empty state: the baseline is whatever came back, captured
 * where it lands.
 *
 * Compared by value — the modals hold arrays and objects rebuilt on every
 * keystroke, so comparing references would call everything a change.
 */
const serialise = (value) => JSON.stringify(value ?? null);

const useBaseline = (initialValue) => {
  // Seeded at mount so a load that never answers still leaves the form usable:
  // the baseline is then the empty state, and typing counts as a change.
  const baseline = useRef(serialise(initialValue));

  return {
    /** Call where the loaded values are applied. */
    capture: (value) => {
      baseline.current = serialise(value);
    },
    hasChanged: (value) => baseline.current !== serialise(value),
    /** The values captured, to show what a change replaces. */
    value: () => JSON.parse(baseline.current),
  };
};

export default useBaseline;
